
import { Component, Ref, Vue } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import Video from './components/video.vue'
import Doc from './components/doc.vue'
import Footer from './components/footer.vue'
import Chat from './components/chat.vue'
import Input from './components/input.vue'
import track from '@xt/client/utils/tracker'
import Popup from '../playback/components/popup.vue'
import { UserInfo } from '@xt/client/entities/user/types'
import { reportEntryLog } from '@xt/client/utils/log'
import { VideoLog } from '@xt/client/entities/gensee/log'
import { GenseeLinkSearch, parseGenseeLink } from '@xt/client/entities/gensee/utils'
import { enterFullScreen, exitFullScreen } from '@xt/client/helpers/system'

@Observer
@Component({
  components: {
    Video,
    Doc,
    Footer,
    Chat,
    Input,
    Popup
  },
  head() {
    return {
      htmlAttrs: {
        'xmlns:gs': 'http://www.gensee.com/ec'
      }
    }
  },
  async asyncData(ctx) {
    const { classhour } = ctx.query as { classhour: string }
    const tabId = sessionStorage.getItem('tab-id')

    if (!classhour) {
      reportEntryLog('live.page-params.loss', { tabId })
      return ctx.redirect('/')
    }

    // 确保用户登录
    await ctx.store.$storeUser.onUpdatingUserInfo()
    const userInfo: UserInfo = ctx.store.$storeUser.UserInfo

    if (!ctx.store.$storeUser.isLogin || !userInfo) {
      reportEntryLog('live.check-user.fail', { isLoigin: ctx.store.$storeUser.isLogin, tabId })
      return ctx.redirect('/')
    }

    // 获取直播链接
    const liveLink = await ctx.store.$storeCourse.Details.Map.Details.onGolive(classhour)

    if (!liveLink) {
      reportEntryLog('live.get-link.fail', { tabId })
      return ctx.redirect('/')
    }

    // 解析直播链接参数
    const { isGray, gensee, sdk, k, courseId, live } = parseGenseeLink<GenseeLinkSearch>(liveLink)!
    reportEntryLog('live.gensee.debug', { gensee, isGray, tabId, live })

    if (live === false) {
      ctx.redirect(`/playback?classhour=${classhour}`)
      return {
        isSuccess: false
      }
    }

    if (!isGray) {
      location.replace(gensee)
      return
    }

    // 课时详情
    await ctx.store.$storeCourse.Details.Map.Details.onLoading({ id: classhour })

    await ctx.store.$genseeLive.pageInit(courseId)

    ctx.store.$genseeLive.setWidgetParams({
      uid: userInfo.id,
      uname: userInfo.nickname,
      ownerid: sdk,
      k: k,
      classhour
    })

    return { gensee }
  },
  layout: 'clean'
})
export default class LivePageView extends Vue {
  // 是否让视频和文档大小屏幕切换  初始不切换
  toggle: boolean = false
  videoNode: HTMLMediaElement | null = null
  openSoundTimer: NodeJS.Timer | null = null
  // 原直播间地址
  gensee: string = ''
  loading = true

  @Ref()
  playbackFooter: {
    toggleScreenHandler: () => void
    volumeHandler: () => void
  }

  get PageStore() {
    return this.$store.$genseeLive
  }

  get UserStore() {
    return this.$store.$storeUser
  }

  get classhourInfo() {
    return this.$store.$storeCourse.Details.Map.Details.dataSource.classhourInfo
  }

  get liveBackgroundCoverUrl() {
    return (
      this.classhourInfo.original.liveBackgroundCoverUrl || 'https://file.xuantong.cn/images/origin/30bafde0178f99a343b1401f524c923b.png'
    )
  }

  // Doc全屏
  handleDocfullScreen() {
    if (!this.PageStore.loadingStatus.genseeAPIReady) return

    if (this.PageStore.currentFullNode && this.PageStore.currentFullNode === 'video') return

    const targetNode = document.querySelector('#doc-live-widget-box') as HTMLDivElement

    if (this.PageStore.isFullScreen) {
      exitFullScreen()
      this.PageStore.setCurrentFullScreenNode('')
    } else {
      enterFullScreen(targetNode)
      this.PageStore.setCurrentFullScreenNode('doc')
    }
  }

  // Video全屏
  handleVideofullScreen() {
    if (!this.PageStore.loadingStatus.genseeAPIReady) return

    if (this.PageStore.currentFullNode && this.PageStore.currentFullNode === 'doc') return

    const targetNode = document.querySelector('#video-live-widget-box') as HTMLDivElement

    // 再按一下退出
    if (this.PageStore.isFullScreen) {
      exitFullScreen()
      this.PageStore.setCurrentFullScreenNode('')
    } else {
      enterFullScreen(targetNode)
      this.PageStore.setCurrentFullScreenNode('video')
    }
  }

  // 双击区域全屏处理
  onDbClickfullScreen(area: 'doc' | 'video') {
    if (area === 'doc') {
      this.handleDocfullScreen()
    } else if (area === 'video') {
      this.handleVideofullScreen()
    }
  }

  keyDownHandler(e: KeyboardEvent) {
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return

    // 避免和其他输入操作冲突
    if (e.target !== document.body) return

    const codeList = ['ArrowUp', 'ArrowDown', 'Space', 'KeyM', 'KeyF', 'KeyP', 'Tab']

    // 避开其他快捷键
    if (!codeList.includes(e.code)) return

    e.preventDefault()

    switch (e.code) {
      case 'ArrowUp':
        this.PageStore.addVolumeByKey()
        break
      case 'ArrowDown':
        this.PageStore.reduceVolumeByKey()
        break
      case 'Space':
        this.PageStore.toggleVideoPlay()
        break
      case 'KeyF':
        this.handleVideofullScreen()
        break
      case 'KeyP':
        this.handleDocfullScreen()
        break
      case 'Tab':
        // 走控制栏切换-保证同步组件内外toggle状态一致
        this.playbackFooter.toggleScreenHandler()
        break
      case 'KeyM':
        this.playbackFooter.volumeHandler()
        break
    }
  }

  // 切换屏幕
  onToggleScreen(toggle: boolean) {
    this.toggle = toggle
  }

  openSoundHandler() {
    this.PageStore.recoverVolumePlay()
    this.PageStore.resetSoundTimer()
  }

  ignoreSoundHandler() {
    this.PageStore.resetSoundTimer()
  }

  backOldVersionHandler() {
    reportEntryLog(VideoLog.live_user_click_back_to_gensee, { tabId: this.PageStore.currentTabId })
    location.replace(this.gensee)
  }

  logoutToHomeHandler() {
    this.UserStore.clearUserInfo()
    this.PageStore.clearBrowserWatchData()
    this.$nextTick(() => {
      location.replace('/')
    })
  }

  toHomeHandler() {
    this.PageStore.clearBrowserWatchData()
    location.replace('/')
  }

  handleReloadPage(type: 'loading-too-long' | 'sleep') {
    if (type === 'loading-too-long') {
      reportEntryLog(VideoLog.live_loading_too_long_reload, { tabId: this.PageStore.currentTabId })
      this.PageStore.setPageLoadingTooLong(false)
    } else {
      reportEntryLog(VideoLog.live_sleep_wake_up_reload, { tabId: this.PageStore.currentTabId })
      this.PageStore.updatePageSleepWakeUp(false)
    }

    window.location.reload()
  }

  handleKeepWaiting() {
    reportEntryLog(VideoLog.live_loading_too_long_keep_wait, { tabId: this.PageStore.currentTabId })
    this.PageStore.setPageLoadingTooLong(false)
  }

  // 用户点击返回旧版
  handleBackToGensee() {
    reportEntryLog(VideoLog.live_loading_too_long_back_to_gensee, { tabId: this.PageStore.currentTabId })
    this.backOldVersionHandler()
  }

  created() {
    window.addEventListener('keydown', this.keyDownHandler)
  }
  mounted() {
    track.record(this.$ajax)
    this.PageStore.init('live-group')
  }
  destroyed() {
    window.removeEventListener('keydown', this.keyDownHandler)
  }
}
